import React, { useEffect, useState } from "react";
import "components/Project-detail-shared/project-detail-shared.scss";
// Inherit styles from existing components
import "components/Project-challenge-solution-results/project-challenge-solution-results.scss";
import "components/Project-architecture/project-architecture.scss";
import "./project-alloggiati-extras.scss";

const ProjectAlloggiatiExtras = ({ caseStudyData }) => {
  const screenshots = caseStudyData?.screenshots || [];
  const flowSteps = caseStudyData?.flowSteps || [];
  const roleCards = caseStudyData?.roleCards || [];
  const hasRole = !!caseStudyData?.evolvansRoleDescription || roleCards.length > 0;

  const [activeScreenshot, setActiveScreenshot] = useState(null);

  useEffect(() => {
    if (!activeScreenshot) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setActiveScreenshot(null);
    };
    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [activeScreenshot]);

  return (
    <>
      {/* 1. Screenshots Section */}
      {screenshots.length > 0 && (
        <section className="pd-section section-padding pd-screenshots-section">
          <div className="container">
            <div className="section-head mb-40">
              <h2 className="pb-2">{caseStudyData?.screenshotsSectionTitle || "Module Screenshots"}</h2>
              <p className="pd-section-desc">
                {caseStudyData?.screenshotsDescription || "A look inside the interfaces."}
              </p>
            </div>
            <div className="ss-grid">
              {screenshots.map((item, idx) =>
                item.img ? (
                  <div
                    className="ss-card wow fadeInUp"
                    data-wow-delay={`${idx * 0.1}s`}
                    key={idx}
                  >
                    <button
                      type="button"
                      className="ss-card__trigger"
                      onClick={() => setActiveScreenshot(item)}
                      aria-label={`View larger screenshot${item.title ? `: ${item.title}` : ""}`}
                    >
                      <span className="ss-card__chrome" aria-hidden="true">
                        <span className="ss-dot ss-dot--a"></span>
                        <span className="ss-dot ss-dot--b"></span>
                        <span className="ss-dot ss-dot--c"></span>
                      </span>
                      <span className="ss-card__screen">
                        <img src={item.img} alt={item.title || "Module screenshot"} loading="lazy" />
                        <span className="ss-card__zoom" aria-hidden="true">
                          <i className="fas fa-expand"></i>
                        </span>
                      </span>
                    </button>
                    {item.title && <p className="ss-card__caption">{item.title}</p>}
                  </div>
                ) : null
              )}
            </div>
          </div>
        </section>
      )}

      {activeScreenshot && (
        <div
          className="ss-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={activeScreenshot.title || "Screenshot preview"}
          onClick={() => setActiveScreenshot(null)}
        >
          <button
            type="button"
            className="ss-lightbox__close"
            aria-label="Close preview"
            onClick={() => setActiveScreenshot(null)}
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
          <figure className="ss-lightbox__frame" onClick={(e) => e.stopPropagation()}>
            <img src={activeScreenshot.img} alt={activeScreenshot.title || "Module screenshot"} />
            {activeScreenshot.title && (
              <figcaption className="ss-lightbox__caption">{activeScreenshot.title}</figcaption>
            )}
          </figure>
        </div>
      )}

      {/* 2. Architecture Diagram (Flow) */}
      {flowSteps.length > 0 && (
        <section className="pd-section section-padding pd-flow-section">
          <div className="container">
            <div className="section-head mb-40">
              <h2 className="pb-2">{caseStudyData?.flowSectionTitle || "Integration Flow"}</h2>
              <p className="pd-section-desc">
                {caseStudyData?.flowDescription || "The lightweight, secure data pipeline."}
              </p>
            </div>
            <div className="pd-solution-stepper align-items-center">
              {flowSteps.map((card, idx) => (
                <React.Fragment key={idx}>
                  <div className="pd-solution-step wow fadeInRight flex-grow-1" data-wow-delay={`${idx * 0.1}s`}>
                    <div className="pd-step-icon">
                      {card.customIcon ? (
                        <img src={card.customIcon} alt={card.title} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                      ) : card.icon ? (
                        <i className={`fas ${card.icon}`}></i>
                      ) : null}
                    </div>
                    <div className="pd-step-content">
                      <h5>{card.title}</h5>
                      <p>{card.text}</p>
                    </div>
                  </div>
                  {idx < flowSteps.length - 1 && (
                    <div className="d-none d-lg-flex align-items-center justify-content-center wow fadeInRight flow-arrow" data-wow-delay={`${idx * 0.1 + 0.05}s`}>
                      <i className="fas fa-chevron-right"></i>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 3. Evolvan's Role */}
      {hasRole && (
        <section className="pd-section section-padding pd-role-section">
          <div className="container">
            <div className="section-head mb-40">
              <h2 className="pb-2">Evolvan's Role</h2>
              {caseStudyData?.evolvansRoleDescription && (
                <p className="pd-section-desc">{caseStudyData.evolvansRoleDescription}</p>
              )}
            </div>
            <div className="row mb-40">
              {roleCards.map((card, idx) => (
                <div className="col-lg-6 mb-30" key={idx}>
                  <div className="pd-card pd-grid-card h-100 wow fadeInUp" data-wow-delay={`${idx * 0.1}s`}>
                    <div className="pd-icon-warning role-icon">
                      {card.customIcon ? (
                        <img src={card.customIcon} alt={card.title} style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                      ) : card.icon ? (
                        <i className={`fas ${card.icon}`}></i>
                      ) : null}
                    </div>
                    <div className="pd-grid-card-content">
                      <h4>{card.title}</h4>
                      <p>{card.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ProjectAlloggiatiExtras;
